export const TRADING_EXPERIENCES = ['professional', 'some', 'new'] as const
export type TradingExperience = (typeof TRADING_EXPERIENCES)[number]

export const WELCOME_TIPS = [
  'palette',
  'shortcuts',
  'linkGroups',
  'templates',
  'riskSlides',
  'orderForm',
  'connect',
  // The walkthrough of a page the tour just set up (PAGE_TOUR_TIPS).
  'watchlist',
  'chart',
  'balance',
  'positions',
  'addAccount',
  // The plan's own steps, after the basics (PLAN_TIPS in welcome-plan.ts).
  'plan',
  'riskSlidesNav',
  'hedgeNav',
  'arbNav',
  'team',
  'tutorials',
] as const
export type WelcomeTip = (typeof WELCOME_TIPS)[number]

/**
 * The tips shown after the experience question, in display order. Professionals get the
 * power-user surfaces first (palette, shortcuts, risk); newcomers start with connecting an
 * account and the guided surfaces.
 */
const TIPS_BY_EXPERIENCE: Record<TradingExperience, readonly WelcomeTip[]> = {
  professional: ['palette', 'shortcuts', 'linkGroups', 'templates', 'riskSlides'],
  some: ['templates', 'linkGroups', 'orderForm', 'palette', 'connect'],
  new: ['connect', 'templates', 'orderForm', 'linkGroups', 'palette'],
}

export function welcomeTipsFor(experience: TradingExperience): readonly WelcomeTip[] {
  return TIPS_BY_EXPERIENCE[experience]
}

/**
 * The template the tour offers to set up before its steps: a simple page for newcomers, the
 * full trading page for some experience, nothing for professionals (they get the templates
 * screen on an empty page anyway).
 */
const SETUP_TEMPLATE_BY_EXPERIENCE: Record<TradingExperience, string | null> = {
  professional: null,
  some: 'trading',
  new: 'starter',
}

export function setupTemplateFor(experience: TradingExperience): string | null {
  return SETUP_TEMPLATE_BY_EXPERIENCE[experience]
}

/**
 * The walkthrough once the tour has set up a page: its widgets in reading order, then where
 * to add an account. Both offered templates (Starter, Trading) have every one of these widgets.
 */
export const PAGE_TOUR_TIPS: readonly WelcomeTip[] = [
  'watchlist',
  'chart',
  'orderForm',
  'balance',
  'positions',
  'addAccount',
]

export function isTradingExperience(value: unknown): value is TradingExperience {
  return typeof value === 'string' && (TRADING_EXPERIENCES as readonly string[]).includes(value)
}

/**
 * Whether the guide should open by itself: never once dismissed, and never under browser
 * automation (Playwright / WebDriver set `navigator.webdriver`), so e2e runs don't hit it.
 */
export function shouldAutoOpenWelcome(dismissed: boolean, webdriver: boolean | undefined) {
  return !dismissed && !webdriver
}
